// ══════════════════════════════════════════════════════════════════
//  적 종 편성 + 스프라이트 계층.
//  종별 밤 편성표와 결정적 추첨기는 World도 읽는다. 종을 고른 뒤의 충돌·HP·드랍은
//  여전히 config.js와 world.js가 소유하며, 프레임·앵커·아틀라스는 이 파일이 소유한다.
//  `_fx/_ph`는 렌더 전용이고, `species`만 메뚜기처럼 종 고유 행동을 여는 게임 필드다.
//
// ── 실물 검수 결과 (2026-07-28, 96프레임 전수) ─────────────────────
//   · sheet-4x2.png = 정확히 4열×2행. 개별 프레임 PNG와 **바이트 동일**(96/96).
//   · **행 의미: 위=오른쪽 / 아래=왼쪽** — 문서와 일치. 검증 방법은 눈 픽셀이다.
//     빨간 눈(#C4402F)은 머리에 있으므로 `눈 x − 몸 무게중심 x` 의 부호가 곧 방향이다.
//     12종 × 8프레임 전부에서 row0 = 양수(+4.9 ~ +44.9) · row1 = 음수(−5.4 ~ −46.2).
//   · 알파 **완전 이진** — 96프레임에서 0/255 아닌 픽셀 **0개**.
//   · 눈 **정확히 1픽셀** — 96프레임 전부. (→ 아래 「눈 채널」 경고)
//   · 좌향 프레임은 우향의 **거울이 아니다**(12종 전부 불일치). 따로 그린 것이므로
//     flipX 로 대체할 수 없다. 시트의 두 행을 그대로 쓴다.
//   · 앵커 — 문서의 *"anchor remains stable across all eight frames"* 는
//     **bbox로 재면 거짓, 몸으로 재면 참**이다:
//       지상종 bbox 하단 = 8프레임 완전 고정(들쥐 43 / 늑대 59 / 곰 119 / 사자 239~240)
//       비행종(박쥐·까마귀·독수리)은 날개 때문에 bbox가 흔들리지만 **눈 y가 셀 정중앙에
//       정확히 고정**(박쥐·까마귀 24/48 · 독수리 64/128) — 문서의 "center anchor"가 맞다.
// ══════════════════════════════════════════════════════════════════

// 4열×2행의 열 순서. 0 = 대기, 1~3 = 걷기.
export const DIR_ROW = { right: 0, left: 1 };

// ── 12종 실측표 ───────────────────────────────────────────────────
// cell/span/anchorY 는 전부 **실측값**이다. 지어낸 수 없음.
//   span   = 8프레임 bbox 최대 변(로스터 문서의 "Maximum span"과 대조 → 12/12 일치)
//   anchorY= 그 종의 논리 좌표(e.x,e.y)가 놓일 셀 안의 y (px)
//            지상종 = (bbox 하단+1) − (bbox 높이 중앙값)/2  → **몸의 세로 중앙**
//            비행종 = 셀 정중앙 (눈 y 실측이 정확히 여기다)
//   ※ 지상종을 「발끝」이 아니라 「몸 중앙」에 맞춘 이유는 아래 ANCHOR_NOTE 참조.
import { NIGHTS, cardIdOf } from './config.js';

export const SPECIES = {
  'field-rat':       { dir: 'field-rat',       ko: '들쥐',     tier: 'swarm', cell: 48,  span: 24, anchorY: 38.5, fly: false, spawnable: true },
  'jackal':          { dir: 'jackal',          ko: '자칼',     tier: 'swarm', cell: 48,  span: 24, anchorY: 36.5, fly: false, spawnable: true },
  'grasshopper':     { dir: 'grasshopper',     ko: '메뚜기',   tier: 'swarm', cell: 48,  span: 24, anchorY: 38.0, fly: false, spawnable: true },
  'bat':             { dir: 'bat',             ko: '박쥐',     tier: 'swarm', cell: 48,  span: 24, anchorY: 24.0, fly: true,  spawnable: true },
  'crow':            { dir: 'crow',            ko: '까마귀',   tier: 'swarm', cell: 48,  span: 24, anchorY: 24.0, fly: true,  spawnable: true },
  // art:'v2' = 읽힘 개정판을 쓴다(아래 READ_NOTE ①). 없으면 v1. cell/span/anchorY 는 v1 과 동일하다.
  'wild-dog':        { dir: 'wild-dog',        ko: '들개',     tier: 'swarm', cell: 48,  span: 24, anchorY: 37.25, fly: false, spawnable: true, art: 'v2' },
  'wolf':            { dir: 'wolf',            ko: '늑대',     tier: 'elite', cell: 64,  span: 40, anchorY: 48.0, fly: false, spawnable: true },
  'wild-dog-leader': { dir: 'wild-dog-leader', ko: '우두머리', tier: 'elite', cell: 64,  span: 40, anchorY: 45.25, fly: false, spawnable: true, art: 'v2' },
  'hyena':           { dir: 'hyena',           ko: '하이에나', tier: 'hyena', cell: 64,  span: 40, anchorY: 46.0, fly: false, spawnable: true },
  // ── 별도 상태기계가 소유하는 종 ────────────────────────────────
  //   독수리 = World.eagles 공중 위협층. 지상 enemies 배열·안정성·동시 상한과 분리한다.
  //   곰·사자 = World.boss 보스 상태기계. 일반 종 추첨에는 들어가지 않는다.
  'eagle':           { dir: 'eagle',           ko: '독수리',   tier: 'threat', cell: 128, span: 96,  anchorY: 64.0,  fly: true,  spawnable: false },
  'bear':            { dir: 'bear',            ko: '곰',       tier: 'boss',   cell: 128, span: 96,  anchorY: 82.75, fly: false, spawnable: false },
  'lion':            { dir: 'lion',            ko: '사자',     tier: 'boss',   cell: 256, span: 176, anchorY: 181.5, fly: false, spawnable: false },
};

// ── ANCHOR_NOTE — 지상종을 「몸 중앙」에 놓은 근거 ─────────────────
// 로스터 문서는 지상종 앵커를 bottom-center 라고 적고, 실제로 bbox 하단이 8프레임
// 고정이므로 **그 말은 참이다**. 그런데 그것을 그대로 논리 좌표에 쓰면 안 된다:
//   · 60도 위에서 본 네발짐승은 화면에서 **눕는다.** 실측 bbox 높이가 물량층 10~17px 인데
//     폭은 21~24px 이다. 발은 몸 전체에 흩어져 있고 「발끝」이라는 한 점이 없다.
//   · 그 상태에서 발끝을 e.y 에 두면 몸 전체가 히트박스(반지름 9) **위로** 올라가고,
//     원으로 그리던 구 회색박스와 「보이는 것 ↔ 맞는 것」의 관계가 달라진다.
//     10-B 측정은 전부 그 관계 위에서 사람이 판단해 왔다.
//   · 그래서 **구 원의 중심 = 몸의 중심**이 되게 맞춘다. 물리는 한 글자도 안 변하고
//     화면에서 읽히는 관계도 안 변한다. 바뀌는 것은 모양뿐이다.
// (플레이어는 서 있는 사람이라 발끝 앵커 0.9375가 맞다 — 종류가 다르다.)

// 논리 좌표에 앵커를 맞추기 위한 draw 오프셋. origin(0.5,0.5) 기준.
// Alpha boundary at the bottom of each source frame. This is deliberately kept
// separate from the simulation anchor: e.x/e.y remains the collision/body
// centre, while contact shadows use the actual rendered feet.
export const FRAME_ALPHA_BOTTOM = Object.freeze({
  'field-rat': 44,
  'jackal': 44,
  'grasshopper': 44,
  'bat': 28,
  'crow': 39,
  'wild-dog': 44,
  'wolf': 60,
  'wild-dog-leader': 60,
  'hyena': 60,
  'eagle': 96,
  'bear': 120,
  'lion': 240,
});

export function anchorOffsetY(sp) { return SPECIES[sp].cell / 2 - SPECIES[sp].anchorY; }

// Render-space offset from an entity's logical y to the bottom alpha boundary.
// `spriteOffsetY` is zero for boss sprites (their y is already the draw anchor)
// and defaults to the normal enemy body-centre correction.
export function spriteBottomOffsetY(
  sp,
  scale = 1,
  originY = 0.5,
  spriteOffsetY = anchorOffsetY(sp),
) {
  const spec = SPECIES[sp];
  const bottom = FRAME_ALPHA_BOTTOM[sp] ?? spec.cell;
  return spriteOffsetY + (bottom - spec.cell * originY) * scale;
}

// ══════════════════════════════════════════════════════════════════
//  READ_NOTE — 밤 화면 「읽힘」 실측 (2026-07-28)
//  자 : 휘도 = BT.601 루마(0.299R+0.587G+0.114B). 이 식이 필드 문서의
//       Mean luminance 6장을 소수 첫째 자리까지 그대로 재현한다(6/6) — 검증된 자다.
//       갉아먹힘 = 스프라이트 픽셀 중 그 아래 필드 픽셀과 |ΔL| < 6 인 비율.
//       **문턱 6은 근거 문서가 없는 임의값**이므로 평균|ΔL| 을 항상 같이 본다.
//       표본 = 8프레임 × 6필드 × 필드 전역 48px 격자 주사(한 자리 결과에 안 흔들리게).
//  대조 이미지 : assets/art/readability-v2/
// ──────────────────────────────────────────────────────────────────
//  ① 들개 계열 — **고쳤다.** v1 시트는 그대로 두고 v2 를 새로 만들었다.
//     원인은 색이 아니라 값이었다. 들개 v1 은 몸의 52.3% 가 L=44.2(#2E2B2E)와
//     L=48.5(#3F2C22)에 몰려 있었고, 이 둘은 서로 4.3 밖에 안 떨어진 **고원**이며
//     하필 필드 평균대(45.7~55.9)의 한복판이다.
//     고침 = **팔레트 한 항목만 이동** #3F2C22(L=48.5, 몸의 31.8%) → #14100D(L=16.9).
//       알파·형태·프레임 배치·눈 위치·span·anchorY 전부 불변(전수 검증).
//       색은 v1 이 이미 쓰던 색이므로 팔레트를 벗어나지 않는다(창작 색 0개).
//       들개        갉 27.0% → 9.3%   평균|ΔL| 19.3 → 27.4
//       우두머리    갉 21.9% → 7.4%   평균|ΔL| 24.2 → 31.0   (기준종 늑대 10.2% / 34.7)
//     **문턱**: 갉 > 20% **그리고** 평균|ΔL| < 25 — 둘 다 실패한 종만 고쳤다.
//       두 자를 동시에 쓰는 이유는 「몸통 전체가 밴드에 있는 종」과 「그림자 한 단만
//       밴드에 걸친 종」을 갉아먹힘 단독으로는 못 가르기 때문이다(자칼 19.7%/29.7 = 후자).
//       9종 중 두 자 모두 실패한 것은 들개 계열 둘뿐이었다.
// ──────────────────────────────────────────────────────────────────
//  ② 적의 눈 — **눈 안와를 고쳤다.**
//     실기 배율 0.485에서 구 1픽셀 동공의 생존율은 24%였다. 동공을 키우거나 빛내면
//     "1 논리픽셀 · 무발광" 계약을 깨므로, 12종 × 8프레임에서 동공의 위치·색·크기와
//     알파·실루엣은 그대로 두고 **이미 불투명한 8이웃 얼굴 픽셀만** 기존 외곽선색
//     #050202로 낮췄다. 눈이 순수 색점이 아니라 어두운 안와 안의 색점으로 읽힌다.
//     결과는 assets/art/readability-v3/eye-socket-12-species-v1.png.
//     검증: 96/96 프레임에 #C4402F 정확히 1픽셀, 비이진 알파 0, 신규 색 0, 발광 0.
// ──────────────────────────────────────────────────────────────────
//  ③ 층별 크기 비 — **아무것도 안 바꿨다.** 자가 틀렸다는 것이 결론이다.
//     span(= max bbox 변)은 네발짐승의 **길이**다. 원(충돌원)과 비교할 값이 아니다.
//     같은 면적의 원 지름(등가 지름)으로 다시 재면:
//       늑대   등가 23.8 / 충돌 26 = 0.915
//       하이에나 등가 26.7 / 충돌 28 = 0.955   ← 늑대보다 등가가 **12% 크다**
//       우두머리 등가 27.0 / 충돌 26 = 1.037
//     하이에나 충돌원이 늑대보다 7.7% 큰 것과 **방향도 크기도 맞는다.**
//     「하이에나 스프라이트가 늑대와 같다」는 span 만 봤을 때의 착시다(둘 다 40 = 길이).
//     기준층을 굳이 정하면 **정예층(늑대 0.915)** 이다 — 세 종이 0.92~1.04 로 모여 있고
//     span 40 이 1.25유닛과 "approach David's height" 로 이중 고정돼 있다.
//     실제로 어긋난 곳은 층 **사이**다: 물량층 등가/충돌 0.63~0.79 vs 정예층 0.92~1.04.
//     물량층은 그려진 몸보다 히트박스가 26~60% 넓다(맞을 것 같지 않은데 맞는 방향).
//     이것을 아트로 맞추려면 물량층을 1.16~1.46배 키워야 하고 그러면 span 24 → 28~34 가 되어
//     ⓐ 0.75유닛 고정과 "half David's linear span" 이 깨지고
//     ⓑ 물량:정예 크기 격차가 1.67배 → 1.21배로 줄어 *"층별 차이를 과장하라"* 와 정면 충돌한다.
//     → **계약을 이기는 판단이므로 안 했다.** ENEMY[].r 쪽 조정은 정본이라 손댈 수 없다.
//        이 항목은 아트가 아니라 **설계 결정**이며 사장님 판단 대기다.
// ══════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════
//  밤별 종 로스터 — **정본 = 30밤시트 각 밤 카드의 「적 구성」 줄**
// ══════════════════════════════════════════════════════════════════
// 아래 `w`(가중치)는 **config.NIGHTS[].spawn 을 종별로 분해한 기저값**이다.
// 검산: 각 층의 `w` 합은 그 카드의 config.spawn 과 정확히 같다. 종 안의 비율은
// 30밤시트 「적 구성」의 절대수에서 가져왔다.
//   예) N8 카드 「자칼 1,205 / 들쥐 1,125 / 늑대 98」
//       기저 420·392·34  ×2.87 = 1205.4 · 1125.0 · 97.6  ✔
// 즉 이 표는 카드에서 읽은 것이고 **비율만** 쓴다 — 절대수는 여전히 world 가 정한다.
// 폐기된 N16·N21·N22는 실행 카드가 아니므로 넣지 않는다. 아래 27장은 DAY_PLAN 이
// 실제로 쓰는 정본 카드 전부이며, 하나라도 빠지면 `speciesRosterForNight` 가 즉시 실패한다.
export const NIGHT_SPECIES = {
  N1: {
    card: '들쥐 1,836 / **늑대 스폰 3 고정(스크립트)** — 총 킬 ~1,839, 동시 380.',
    swarm: { 'field-rat': 290 },
    elite: { 'wolf': 3 },
    hyena: {},
    // ⚠ 카드는 *"이 밤만 정예층이 밤 스폰 배수 밖이다 … 배수를 먹이면 늑대가 열아홉이
    //   되어 시그니처 일기가 그 자리에서 거짓이 된다"* 고 못박는다.
    //   config.NIGHTS.N1.multExempt + spawnBudgetOf()가 이 세 마리를 배수 밖에 둔다.
    //   이 표도 elite 기저 합을 정확히 3으로 보존해 렌더 종 비율이 그 계약을 따른다.
    eliteScriptFixed: 3,
  },
  N2: {
    card: '들쥐 1,220 / 늑대 47. ~1,267, 동시 233.',
    swarm: { 'field-rat': 366 },
    elite: { 'wolf': 14 },
    hyena: {},
  },
  N3: {
    card: '들쥐 1,179 / 들개 590 / 우두머리 59 / 늑대 20. ~1,848, 동시 314.',
    swarm: { 'field-rat': 300, 'wild-dog': 150 },
    elite: { 'wild-dog-leader': 15, 'wolf': 5 },
    hyena: {},
  },
  N4: {
    card: '들쥐 754 / 들개 411 / 우두머리 55 / 늑대 59. ~1,279, 동시 217.',
    swarm: { 'field-rat': 330, 'wild-dog': 180 },
    elite: { 'wild-dog-leader': 24, 'wolf': 26 },
    hyena: {},
  },
  N5: {
    card: '들쥐 617 / 자칼 843 / 늑대 69. ~1,529, 동시 247.',
    swarm: { 'field-rat': 250, 'jackal': 342 },
    elite: { 'wolf': 28 },
    hyena: {},
  },
  N6: {
    card: '메뚜기 921 / 들쥐 368 / 자칼 103 / 늑대 44. ~1,436, 동시 258.',
    swarm: { 'grasshopper': 500, 'field-rat': 200, 'jackal': 56 },
    elite: { 'wolf': 24 },
    hyena: {},
  },
  N7: {
    card: '자칼 855 / 들쥐 513 / 늑대 64. ~1,432, 동시 256. (독수리는 실루엣 예고뿐)',
    swarm: { 'jackal': 400, 'field-rat': 240 },
    elite: { 'wolf': 30 },
    hyena: {},
  },
  N8: {
    card: '자칼 1,205 / 들쥐 1,125 / 늑대 98 / 독수리 4. ~2,432, 동시 373.',
    swarm: { 'jackal': 420, 'field-rat': 392 },
    elite: { 'wolf': 34 },
    hyena: {},
    absent: '독수리 4 — 공중 유닛이라 회색박스 모델 밖(config.threatCut = 4로 이미 제외됨)',
  },
  N9: {
    card: '자칼 820 / 들쥐 586 / 늑대 51 / 독수리 5. ~1,462, 동시 220.',
    swarm: { 'jackal': 560, 'field-rat': 400 },
    elite: { 'wolf': 35 },
    hyena: {},
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N10: {
    card: '들쥐 1,470 / 자칼 980 / 늑대 110 / 독수리 5. ~2,565, 동시 368.',
    swarm: { 'field-rat': 600, 'jackal': 400 },
    elite: { 'wolf': 45 },
    hyena: {},
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N11: {
    card: '박쥐 602 / 들쥐 542 / 자칼 241 / 늑대 54 / 독수리 5. ~1,444, 동시 205.',
    swarm: { 'bat': 499, 'field-rat': 450, 'jackal': 200 },
    elite: { 'wolf': 45 },
    hyena: {},
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N12: {
    card: '박쥐 985 / 들쥐 985 / 자칼 473 / 하이에나 79 / 늑대 30 / 독수리 5. ~2,557, 동시 355.',
    swarm: { 'bat': 500, 'field-rat': 500, 'jackal': 240 },
    elite: { 'wolf': 15 },
    hyena: { 'hyena': 40 },
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N13: {
    card: '박쥐 594 / 들쥐 594 / 자칼 89 / 하이에나 52 / 독수리 5. ~1,334, 동시 208.',
    swarm: { 'bat': 400, 'field-rat': 400, 'jackal': 60 },
    elite: {},
    hyena: { 'hyena': 35 },
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N14: {
    card: '박쥐 600 / 들쥐 550 / 자칼 280 / 하이에나 45 / 늑대 17 / 독수리 8. ~1,500, 동시 200.',
    swarm: { 'bat': 600, 'field-rat': 550, 'jackal': 280 },
    elite: { 'wolf': 17 },
    hyena: { 'hyena': 45 },
    absent: '독수리 8 — 공중 유닛(threatCut = 8)',
  },
  N15: {
    card: '들쥐 1,330 / 자칼 950 / 박쥐 361 / 하이에나 57 / 늑대 48 / 독수리 5. ~2,751, 동시 361.',
    swarm: { 'field-rat': 700, 'jackal': 500, 'bat': 190 },
    elite: { 'wolf': 25 },
    hyena: { 'hyena': 30 },
    absent: '독수리 5 — 공중 유닛(threatCut = 5)',
  },
  N17: {
    card: '자칼 434 / 들쥐 130 / 늑대 14. ~578, 동시 101.',
    swarm: { 'jackal': 300, 'field-rat': 90 },
    elite: { 'wolf': 10 },
    hyena: {},
  },
  N18: {
    card: '들쥐 976 / 자칼 671 / 박쥐 281 / 하이에나 43 / 늑대 33 / 독수리 8. ~2,012, 동시 256.',
    swarm: { 'field-rat': 800, 'jackal': 550, 'bat': 230 },
    elite: { 'wolf': 27 },
    hyena: { 'hyena': 35 },
    absent: '독수리 8 — 공중 유닛(threatCut = 8)',
  },
  N19: {
    card: '까마귀 990 / 들쥐 708 / 자칼 609 / 하이에나 57 / 늑대 31 / 독수리 8. ~2,403, 동시 311.',
    swarm: { 'crow': 700, 'field-rat': 500, 'jackal': 430 },
    elite: { 'wolf': 22 },
    hyena: { 'hyena': 40 },
    absent: '독수리 8 — 공중 유닛(threatCut = 8)',
  },
  N20: {
    card: '까마귀 1,301 / 들쥐 977 / 자칼 803 / 하이에나 109 / 늑대 43. ~3,233, 동시 434.',
    swarm: { 'crow': 600, 'field-rat': 450, 'jackal': 370 },
    elite: { 'wolf': 20 },
    hyena: { 'hyena': 50 },
  },
  N23: {
    card: '까마귀 1,336 / 들쥐 919 / 자칼 718 / 하이에나 117 / 늑대 50. ~3,140, 동시 434.',
    swarm: { 'crow': 800, 'field-rat': 550, 'jackal': 430 },
    elite: { 'wolf': 30 },
    hyena: { 'hyena': 70 },
  },
  N24: {
    card: '까마귀 1,009 / 들쥐 649 / 자칼 505 / 하이에나 87 / 늑대 36. ~2,286, 동시 317.',
    swarm: { 'crow': 700, 'field-rat': 450, 'jackal': 350 },
    elite: { 'wolf': 25 },
    hyena: { 'hyena': 60 },
  },
  N25: {
    card: '까마귀 923 / 들쥐 719 / 자칼 565 / 하이에나 92 / 늑대 36. ~2,335, 동시 308.',
    swarm: { 'crow': 899, 'field-rat': 701, 'jackal': 551 },
    elite: { 'wolf': 35 },
    hyena: { 'hyena': 90 },
  },
  N26: {
    card: '까마귀 914 / 들쥐 741 / 자칼 627 / 하이에나 80 / 늑대 23. ~2,385, 동시 319.',
    swarm: { 'crow': 801, 'field-rat': 649, 'jackal': 550 },
    elite: { 'wolf': 20 },
    hyena: { 'hyena': 70 },
  },
  N27: {
    card: '들쥐 715 / 자칼 612 / 까마귀 510 / 박쥐 479 / 하이에나 82 / 늑대 21 / 우두머리 15 / 독수리 8. ~2,442, 동시 326.',
    swarm: { 'field-rat': 701, 'jackal': 600, 'crow': 500, 'bat': 470 },
    elite: { 'wolf': 20, 'wild-dog-leader': 15 },
    hyena: { 'hyena': 81 },
    absent: '독수리 8 — 공중 유닛(threatCut = 8)',
  },
  N28: {
    card: '까마귀 967 / 들쥐 830 / 자칼 581 / 하이에나 97 / 늑대 7 / 곰 1. ~2,483, 동시 360.',
    swarm: { 'crow': 699, 'field-rat': 600, 'jackal': 420 },
    elite: { 'wolf': 5 },
    hyena: { 'hyena': 70 },
    absent: '곰 1 — 보스 상태기계가 별도로 처리',
  },
  N29: {
    card: '까마귀 925 / 들쥐 809 / 자칼 670 / 하이에나 116 / 늑대 12 / 사자 1. ~2,533, 동시 347.',
    swarm: { 'crow': 800, 'field-rat': 700, 'jackal': 580 },
    elite: { 'wolf': 10 },
    hyena: { 'hyena': 101 },
    absent: '사자 1 — 보스 상태기계가 별도로 처리',
  },
  N30: {
    card: '들쥐 875 / 까마귀 875 / 자칼 750 / 박쥐 550 / 하이에나 138 / 늑대 38 / 독수리 9 / **곰 1**. ~3,236, 동시 438.',
    swarm: { 'field-rat': 700, 'crow': 700, 'jackal': 600, 'bat': 440 },
    elite: { 'wolf': 30 },
    hyena: { 'hyena': 110 },
    absent: '독수리 9(공중 · threatCut = 9) · 곰 1(보스 상태기계 없음)',
  },
};
// 스폰 대상 9종(아틀라스에 굽는 것). 메뚜기는 카드 N6(현재 8일)부터 실제로 뽑힌다.
export const ATLAS_SPECIES = Object.keys(SPECIES).filter(k => SPECIES[k].spawnable);
// 준비만 하는 3종. 스폰 표에 없고 아틀라스에도 없다(배치 텍스처를 오염시키지 않는다).
export const PREPARED_ONLY = Object.keys(SPECIES).filter(k => !SPECIES[k].spawnable);

// ── 걷기 프레임율 ─────────────────────────────────────────────────
// **지어낸 상수 없음.** 유일한 기준점은 이미 코드에 있는 플레이어의 WALK_FPS = 6이고,
// 규칙은 「몸 길이당 걸음 수를 종에 상관없이 같게 둔다」 하나다:
//     fps(종) = 6 × (그 층 이속 / 그 종 span) ÷ (물량층 이속 62 / 물량층 span 24)
// 그러면 어느 종이든 **한 걸음 주기에 제 몸 길이의 같은 배수만큼 나아간다**(발이 안 미끄러진다).
//   물량층 62px/s · span 24 → 6.00 fps
//   정예층 54px/s · span 40 → 3.14 fps  (늑대의 "stalking walk" — 느린 것이 맞다)
//   하이에나 76px/s · span 40 → 4.41 fps
export const MASS_REF = { speed: 62, span: 24, fps: 6 };
export function walkFpsFor(speed, span) {
  return MASS_REF.fps * (speed / span) / (MASS_REF.speed / MASS_REF.span);
}

// ── 아틀라스 굽기 ─────────────────────────────────────────────────
// **단일 텍스처 = 단일 배치.** 9종의 보행 8프레임과 물리 공격 8프레임을
// 한 캔버스에 눕힌다. 공격 시트도 같은 셀·좌우 행·이진 알파·종 팔레트를 쓴다.
// 배치는 종마다 보행 4×2 바로 아래 공격 4×2를 붙여 세로로 쌓는다.
// 독수리·곰·사자를 여기 넣으면 2048×992 ≈ 8MB가 되고 **한 번도 안 그리는 텍스처가
// 배치 텍스처를 차지한다.** 그래서 그 셋은 별도 텍스처로 따로 둔다(드로우콜 0).
export const ATLAS_KEY = 'enemy-atlas';
export const sheetKeyOf = (sp) => `enemy-sheet-${sp}`;
export const sheetUrlOf = (sp) => `/art/enemies/${SPECIES[sp].dir}/${SPECIES[sp].art ?? 'v1'}/sheet-4x2.png`;
export const attackSheetKeyOf = (sp) => `enemy-attack-sheet-${sp}`;
export const attackSheetUrlOf = (sp) => `/art/enemies/${SPECIES[sp].dir}/attack-v1/sheet-4x2.png`;
// 프레임 이름: `<종>|<r|l><0..3>`  (0 = 대기, 1~3 = 걷기)
export const frameName = (sp, dir, i) => `${sp}|${dir}${i}`;
// 공격 프레임: 0=예비, 1=접촉 진입, 2=과신전/타격, 3=회수.
export const attackFrameName = (sp, dir, i) => `${sp}|attack|${dir}${i}`;

export function bakeEnemyAtlas(scene, Phaser) {
  if (scene.textures.exists(ATLAS_KEY)) return scene.textures.get(ATLAS_KEY);
  let w = 0, h = 0;
  for (const sp of ATLAS_SPECIES) {
    const c = SPECIES[sp].cell;
    w = Math.max(w, c * 4);           // 시트 원본 폭 = 4열
    h += c * 4;                       // 보행 2행 + 공격 2행
  }
  const tex = scene.textures.createCanvas(ATLAS_KEY, w, h);
  const ctx = tex.getContext();
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, w, h);
  let y = 0;
  for (const sp of ATLAS_SPECIES) {
    const c = SPECIES[sp].cell;
    const src = scene.textures.get(sheetKeyOf(sp)).getSourceImage();
    const attack = scene.textures.get(attackSheetKeyOf(sp)).getSourceImage();
    ctx.drawImage(src, 0, y);         // 시트를 통째로 4×2 그대로 얹는다
    ctx.drawImage(attack, 0, y + c * 2);
    for (const [dir, row] of Object.entries(DIR_ROW))
      for (let i = 0; i < 4; i++)
        tex.add(frameName(sp, dir[0], i), 0, i * c, y + row * c, c, c);
    for (const [dir, row] of Object.entries(DIR_ROW))
      for (let i = 0; i < 4; i++)
        tex.add(attackFrameName(sp, dir[0], i), 0,
          i * c, y + c * 2 + row * c, c, c);
    y += c * 4;
  }
  tex.refresh();
  tex.setFilter(Phaser.Textures.FilterMode.NEAREST);
  return tex;
}

// ── 밤별 종 편성 ──────────────────────────────────────────────────
// `NIGHT_SPECIES` 열쇠는 30밤시트의 **구 카드 번호**, 런타임 `nightKey` 는 40일의
// **날짜 번호**다. 둘을 같은 좌표로 읽으면 현재 8일(카드 N6)이 과거 N8 편성을
// 읽는 식으로 데뷔종이 사라진다. 날짜 폴백은 금지하고 cardId 를 반드시 거친다.
const TIERS = ['swarm', 'elite', 'hyena'];
const tierSum = (tbl = {}) => Object.values(tbl).reduce((sum, w) => sum + w, 0);
const draftRosterCache = new Map();

function midpointTier(a = {}, b = {}, target, label) {
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  if (!keys.length) {
    if (target !== 0) throw new Error(`${label}: 빈 종 편성인데 층 예산은 ${target}이다`);
    return {};
  }
  // config.buildNights 와 같은 산술 중간값. 종별 .5 반올림이 층 총량을 한 마리
  // 어긋나게 할 수 있어, 최대 나머지법으로 config 의 층 중간값에 정확히 맞춘다.
  const rows = keys.map((key, order) => {
    const raw = ((a[key] ?? 0) + (b[key] ?? 0)) / 2;
    return { key, order, weight: Math.floor(raw), fraction: raw - Math.floor(raw) };
  });
  let left = target - rows.reduce((sum, row) => sum + row.weight, 0);
  if (left < 0 || left > rows.length)
    throw new Error(`${label}: 중간 편성 ${target}마리를 보존할 수 없다 (나머지 ${left})`);
  const byRemainder = [...rows].sort((x, y) => y.fraction - x.fraction || x.order - y.order);
  for (let i = 0; i < left; i++) byRemainder[i].weight++;
  return Object.fromEntries(rows.filter((row) => row.weight > 0).map((row) => [row.key, row.weight]));
}

/** 40일 날짜가 실제로 쓰는 종 편성. 정본 카드는 직접, 초안은 양옆 정본 카드 중간값. */
export function speciesRosterForNight(nightKey) {
  const night = NIGHTS[nightKey];
  if (!night) throw new Error(`알 수 없는 밤 날짜다 — ${nightKey}`);
  if (night.sabbath) throw new Error(`안식일에는 적 종 편성이 없다 — ${nightKey}`);

  const cardId = cardIdOf(nightKey);
  if (cardId) {
    const roster = NIGHT_SPECIES[cardId];
    if (!roster) throw new Error(`${nightKey}: 정본 카드 ${cardId}의 종 편성이 없다`);
    return roster;
  }
  if (!night.draft) throw new Error(`${nightKey}: 정본 카드도 초안 표식도 없는 전투 밤이다`);
  if (draftRosterCache.has(nightKey)) return draftRosterCache.get(nightKey);

  // buildNights 는 정본 카드를 먼저 넣고 초안을 2차에 넣으므로 객체 삽입 순서는
  // 날짜 순이 아니다. 숫자 날짜로 다시 정렬해야 실제 DAY_PLAN 의 양옆을 찾는다.
  const days = Object.keys(NIGHTS).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
  const at = days.indexOf(nightKey);
  let lo = null, hi = null;
  for (let i = at - 1; i >= 0 && !lo; i--) {
    const id = cardIdOf(days[i]);
    if (id) lo = { id, roster: NIGHT_SPECIES[id] };
  }
  for (let i = at + 1; i < days.length && !hi; i++) {
    const id = cardIdOf(days[i]);
    if (id) hi = { id, roster: NIGHT_SPECIES[id] };
  }
  if (!lo?.roster || !hi?.roster)
    throw new Error(`${nightKey}: 초안의 양옆 정본 종 편성을 찾지 못했다`);

  const roster = {
    card: `초안 보간 — ${lo.id} ↔ ${hi.id} 정본 카드의 종별 기저값 중간`,
    draft: true,
    sourceCards: [lo.id, hi.id],
  };
  for (const tier of TIERS) {
    const target = night.spawn[tier] ?? 0;
    roster[tier] = midpointTier(lo.roster[tier], hi.roster[tier], target,
      `${nightKey}/${tier}`);
    if (tierSum(roster[tier]) !== target)
      throw new Error(`${nightKey}/${tier}: 종 편성 합이 config.spawn ${target}과 다르다`);
  }
  draftRosterCache.set(nightKey, roster);
  return roster;
}

// ── 밤별 종 추첨 ──────────────────────────────────────────────────
// world 는 층(swarm/elite/hyena)만 정한다. 종은 **그 층 안에서 카드 비율대로** 뽑는다.
// 한 번 정해지면 그 개체가 죽을 때까지 안 바뀐다(개체 정체성).
export function makeSpeciesPicker(nightKey) {
  const N = speciesRosterForNight(nightKey);
  const build = (tbl) => {
    const keys = Object.keys(tbl);
    if (!keys.length) return null;
    let tot = 0; const cum = [];
    for (const k of keys) { tot += tbl[k]; cum.push(tot); }
    return { keys, cum, tot };
  };
  const pools = { swarm: build(N.swarm), elite: build(N.elite), hyena: build(N.hyena) };
  const fallback = { swarm: 'field-rat', elite: 'wolf', hyena: 'hyena' };
  return (kind, u) => {
    const p = pools[kind];
    if (!p) return fallback[kind] ?? 'field-rat';
    const r = u * p.tot;
    for (let i = 0; i < p.cum.length; i++) if (r < p.cum[i]) return p.keys[i];
    return p.keys[p.keys.length - 1];
  };
}
